import { PageHeader } from "@/components/layout/page-header";
import { DeleteSetDialog } from "@/components/reconciliation/delete-set-dialog";
import { ListEmptyState } from "@/components/reconciliation/empty-states";
import { StatusBadge } from "@/components/reconciliation/status-badge";
import { useReconciliationToasts } from "@/components/reconciliation/use-reconciliation-toasts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { deleteReconciliationSet, getReconciliationSets } from "@/services/api";
import { reconciliationMessages } from "@/services/messages";
import { useTabActions } from "@/stores/tab-store";
import type { ReconciliationSet, ReconciliationStatus } from "@efes/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MoreHorizontal, Plus } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Координаты и контекст открытого меню действий. Меню рендерится с
 * `position: fixed` вне таблицы — иначе `overflow-x-auto` контейнера
 * клипает его (CSS: задание одной оси overflow != visible делает другую
 * ось implicit auto, а не visible).
 */
interface OpenMenuState {
	setId: number;
	left: number;
	top: number;
	editDisabled: boolean;
	deleteDisabled: boolean;
	tooltip?: string;
}

const STATUS_OPTIONS: Array<{ value: ReconciliationStatus | "all"; labelKey: string }> = [
	{ value: "all", labelKey: "list_filter_status_all" },
	{ value: "draft", labelKey: "status_draft" },
	{ value: "running", labelKey: "status_running" },
	{ value: "completed", labelKey: "status_completed" },
	{ value: "failed", labelKey: "status_failed" },
];

// Sprint 3 / A4: legacy `aggregate-by-work-kind` удалён; миграция 0022
// конвертирует исторические строки в `unified`. Fallback на `level_unified`
// чтобы defensive-render отработал на возможных старых клиентах кеша.
const LEVEL_LABEL_KEY: Record<string, keyof typeof reconciliationMessages> = {
	position: "level_position",
	"grand-total": "level_grand_total",
	unified: "level_unified",
};

const MODE_LABEL_KEY: Record<string, keyof typeof reconciliationMessages> = {
	volume: "mode_volume",
	rate: "mode_rate",
	cost: "mode_cost",
};

function formatDate(value: string): string {
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * Страница списка наборов сверки (T044, US1).
 * - Фильтр по статусу.
 * - Empty state по FR-019a.
 * - Контекстное меню действий: Edit / Delete с блокировкой при running/completed.
 * - Pagination.
 */
export function ReconciliationListPage() {
	const queryClient = useQueryClient();
	const { openReconciliationCreateTab, openReconciliationDetailTab } = useTabActions();
	const toasts = useReconciliationToasts();

	const [page, setPage] = useState(1);
	const [pageSize] = useState(50);
	const [statusFilter, setStatusFilter] = useState<ReconciliationStatus | "all">("all");
	const [pendingDelete, setPendingDelete] = useState<ReconciliationSet | null>(null);
	const [openMenu, setOpenMenu] = useState<OpenMenuState | null>(null);

	useEffect(() => {
		const onMouseDown = (e: MouseEvent) => {
			const target = e.target as HTMLElement | null;
			if (target?.closest("[data-row-menu-root='true']")) return;
			setOpenMenu(null);
		};
		const onScroll = () => setOpenMenu(null);
		const onResize = () => setOpenMenu(null);
		document.addEventListener("mousedown", onMouseDown);
		// Прокрутка контейнера или окна → меню "уплывает" от кнопки;
		// проще закрыть, чем тянуть live-перерасчёт координат.
		window.addEventListener("scroll", onScroll, true);
		window.addEventListener("resize", onResize);
		return () => {
			document.removeEventListener("mousedown", onMouseDown);
			window.removeEventListener("scroll", onScroll, true);
			window.removeEventListener("resize", onResize);
		};
	}, []);

	const { data, isLoading } = useQuery({
		queryKey: ["reconciliation-sets", { page, pageSize, status: statusFilter }],
		queryFn: () =>
			getReconciliationSets({
				page,
				pageSize,
				status: statusFilter === "all" ? undefined : statusFilter,
			}),
	});

	const deleteMutation = useMutation({
		mutationFn: (setId: number) => deleteReconciliationSet(setId),
		onSuccess: () => {
			toasts.showSuccess("deleted");
			queryClient.invalidateQueries({ queryKey: ["reconciliation-sets"] });
			setPendingDelete(null);
		},
		onError: (err) => {
			toasts.showApiError(err);
			setPendingDelete(null);
		},
	});

	const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

	return (
		<div className="space-y-6">
			<PageHeader
				title={reconciliationMessages.page_list_title}
				actions={
					<Button
						type="button"
						onClick={() => openReconciliationCreateTab()}
						className="bg-blue-600 text-white hover:bg-blue-700"
					>
						<Plus className="h-4 w-4" />
						{reconciliationMessages.button_create_set}
					</Button>
				}
			/>

			<div className="flex flex-wrap items-center gap-3">
				<label className="flex items-center gap-2 text-sm text-slate-700">
					<span>{reconciliationMessages.list_filter_status_label}:</span>
					<select
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value as ReconciliationStatus | "all");
							setPage(1);
						}}
						className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					>
						{STATUS_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{reconciliationMessages[opt.labelKey as keyof typeof reconciliationMessages]}
							</option>
						))}
					</select>
				</label>
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center rounded-lg border border-slate-200 bg-white py-16 text-sm text-slate-500">
					<Loader2 className="mr-2 h-4 w-4 animate-spin" /> {reconciliationMessages.loading}
				</div>
			) : !data || data.total === 0 ? (
				<ListEmptyState onCreate={() => openReconciliationCreateTab()} />
			) : (
				<>
					<div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
						<table className="min-w-full divide-y divide-slate-200">
							<thead className="bg-slate-50">
								<tr>
									<Th>{reconciliationMessages.list_column_name}</Th>
									<Th>{reconciliationMessages.list_column_level}</Th>
									<Th>{reconciliationMessages.list_column_modes}</Th>
									<Th>{reconciliationMessages.list_column_status}</Th>
									<Th>{reconciliationMessages.list_column_author}</Th>
									<Th>{reconciliationMessages.list_column_created_at}</Th>
									<Th aria-label={reconciliationMessages.actions_label} />
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-200">
								{data.items.map((set) => {
									// `completed` больше не блокирует Edit/Delete: пользователь
									// вправе перенастроить или удалить набор после прогона; старые
									// run-результаты остаются под собственным runId, новый запуск
									// создаст новый run-record.
									const editDisabled = set.status === "running";
									const deleteDisabled = set.status === "running";
									const tooltip = editDisabled
										? reconciliationMessages.error_status_running
										: undefined;
									const handleRowActivate = (e: React.MouseEvent | React.KeyboardEvent) => {
										const target = e.target as HTMLElement | null;
										if (target?.closest("[data-row-menu='true']")) return;
										openReconciliationDetailTab(set.id, set.name);
									};
									return (
										<tr
											key={set.id}
											className="cursor-pointer hover:bg-slate-50"
											onClick={handleRowActivate}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													e.preventDefault();
													handleRowActivate(e);
												}
											}}
										>
											<Td>
												<span className="text-sm font-medium text-slate-900">{set.name}</span>
											</Td>
											<Td>
												<span className="text-sm text-slate-700">
													{reconciliationMessages[LEVEL_LABEL_KEY[set.level] ?? "level_unified"]}
												</span>
											</Td>
											<Td>
												<div className="flex flex-wrap gap-1">
													{set.modes.map((m) => (
														<Badge key={m} variant="pill" tone="neutral">
															{reconciliationMessages[MODE_LABEL_KEY[m]]}
														</Badge>
													))}
												</div>
											</Td>
											<Td>
												<StatusBadge status={set.status} />
											</Td>
											<Td>
												<span className="text-sm text-slate-700">
													{set.createdByName ?? reconciliationMessages.unknown_user}
												</span>
											</Td>
											<Td>
												<span className="text-sm text-slate-500">{formatDate(set.createdAt)}</span>
											</Td>
											<Td>
												<button
													type="button"
													data-row-menu-root="true"
													onClick={(e) => {
														e.stopPropagation();
														if (openMenu?.setId === set.id) {
															setOpenMenu(null);
															return;
														}
														const menuWidth = 192; // w-48 в Tailwind
														const estimatedHeight = 88; // 2 пункта * 44px
														const viewportPadding = 8;
														const rect = (
															e.currentTarget as HTMLButtonElement
														).getBoundingClientRect();
														let left = rect.right - menuWidth;
														if (left < viewportPadding) left = viewportPadding;
														let top = rect.bottom + 4;
														if (top + estimatedHeight > window.innerHeight - viewportPadding) {
															top = Math.max(viewportPadding, rect.top - estimatedHeight - 4);
														}
														setOpenMenu({
															setId: set.id,
															left,
															top,
															editDisabled,
															deleteDisabled,
															tooltip,
														});
													}}
													className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
													aria-label={reconciliationMessages.actions_label}
												>
													<MoreHorizontal className="h-4 w-4" />
												</button>
											</Td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					<div className="flex items-center justify-between text-sm text-slate-600">
						<div>
							{(data.page - 1) * data.pageSize + 1}–
							{Math.min(data.page * data.pageSize, data.total)} из {data.total}
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page <= 1}
								className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 hover:bg-slate-50"
							>
								{reconciliationMessages.pagination_prev}
							</button>
							<span>
								{page} / {totalPages}
							</span>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page >= totalPages}
								className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 hover:bg-slate-50"
							>
								{reconciliationMessages.pagination_next}
							</button>
						</div>
					</div>
				</>
			)}

			{openMenu &&
				(() => {
					const set = data?.items.find((s) => s.id === openMenu.setId);
					if (!set) return null;
					return (
						<div
							data-row-menu-root="true"
							className="fixed z-50 w-48 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
							style={{ left: openMenu.left, top: openMenu.top }}
						>
							<MenuItem
								disabled={openMenu.editDisabled}
								tooltip={openMenu.tooltip}
								onClick={() => {
									openReconciliationCreateTab(set.id);
									setOpenMenu(null);
								}}
							>
								{reconciliationMessages.button_edit}
							</MenuItem>
							<MenuItem
								disabled={openMenu.deleteDisabled}
								tooltip={openMenu.tooltip}
								danger
								onClick={() => {
									setPendingDelete(set);
									setOpenMenu(null);
								}}
							>
								{reconciliationMessages.button_delete}
							</MenuItem>
						</div>
					);
				})()}

			{pendingDelete && (
				<DeleteSetDialog
					set={pendingDelete}
					onCancel={() => setPendingDelete(null)}
					onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
					isPending={deleteMutation.isPending}
				/>
			)}
		</div>
	);
}

function Th({ children, ...rest }: React.ThHTMLAttributes<HTMLTableCellElement>) {
	return (
		<th
			{...rest}
			className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500"
		>
			{children}
		</th>
	);
}

function Td({ children }: { children?: React.ReactNode }) {
	return <td className="px-4 py-3 align-middle">{children}</td>;
}

function MenuItem({
	disabled,
	tooltip,
	danger,
	onClick,
	children,
}: {
	disabled?: boolean;
	tooltip?: string;
	danger?: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			title={tooltip}
			onClick={onClick}
			className={`block w-full px-4 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
				danger ? "text-red-700 hover:bg-red-50" : "text-slate-700 hover:bg-slate-50"
			}`}
		>
			{children}
		</button>
	);
}
